import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import LeagueFooter from '../components/LeagueFooter'
import DivisionPills from '../components/DivisionPills'
import type { TeamCard, TeamsPageVM } from '../../../core/pages/teams/types'
import { sf } from '../../../core/features/sf'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

// Classic view for the `teams` page; data lives in core/pages/teams/controller.ts.

export default function Teams({ vm }: { vm: TeamsPageVM }) {
  const m = useT(siteClassicMessages).players.teams
  const core = useT(siteCoreMessages)
  const tenantName = vm.leagueName
  const loading = vm.status === 'loading' || vm.refreshing
  const error = vm.error
  const searchQuery = vm.search.value
  const filteredTeams = vm.items

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-dark via-bg-primary to-bg-primary">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-20 sm:pt-24 pb-12 sm:pb-16 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-px w-8 bg-primary/30"></div>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary">
                {tenantName}
              </span>
              <div className="h-px w-8 bg-primary/30"></div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Montserrat'] font-black text-white uppercase tracking-tight leading-none mb-4">
              {core.pages.nav.teams}
            </h1>
            <p className="text-base sm:text-lg text-white/60 font-medium">
              {m.subtitle}
            </p>
            {/* #583: division pill row (renders nothing without 2+ live divisions) */}
            <DivisionPills division={vm.division} className="mt-6" center />
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="relative pb-8">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <svg className="w-5 h-5 text-primary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={m.search}
                value={searchQuery}
                onChange={(e) => vm.search.set(e.target.value)}
                {...sf('teams.search')}
                className="w-full bg-bg-secondary/60 backdrop-blur-md border border-secondary/30 text-white placeholder-white/40 pl-12 pr-4 py-4 focus:outline-none focus:border-primary/50 transition-colors text-base leading-5 sm:leading-6"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Teams Section */}
      <section className="relative pb-24">
        <div className="container mx-auto px-4">
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-primary/30"></div>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50" {...sf('teams.count')}>
                {loading ? core.common.loading : m.count(filteredTeams.length)}
              </span>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 p-6 text-center mb-8" {...sf('state.error')}>
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" {...sf('state.loading')}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-bg-secondary/60 border border-secondary/20 mb-3"></div>
                  <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-white/5 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && filteredTeams.length === 0 && (
            <div className="text-center py-20" {...sf('teams.empty')}>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary/20 border border-secondary/30 mb-6">
                <svg className="w-10 h-10 text-primary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-['Montserrat'] font-black text-white uppercase tracking-tight mb-2">
                {m.noneFound}
              </h3>
              <p className="text-white/50 text-sm mb-6">
                {searchQuery ? m.tryAdjusting : m.noneAvailable}
              </p>
              {searchQuery && (
                <button
                  onClick={() => vm.search.set('')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-secondary/30 border border-secondary text-white hover:bg-secondary/50 hover:border-primary/50 transition-all text-sm font-bold uppercase tracking-wider"
                >
                  {m.clearSearch}
                </button>
              )}
            </div>
          )}

          {/* Teams Grid */}
          {!loading && !error && filteredTeams.length > 0 && (vm.groups ? (
            // #583: "All divisions": one heading per division (a team in two is listed under each).
            <div className="space-y-12" {...sf('teams.list')}>
              {vm.groups.map((group) => (
                <section key={group.division?.id ?? 'none'} aria-labelledby={`teams-division-${group.division?.id ?? 'none'}`}>
                  <h2 id={`teams-division-${group.division?.id ?? 'none'}`} className="mb-5 text-xl sm:text-2xl font-['Montserrat'] font-black text-white uppercase tracking-tight">
                    {group.division?.name ?? m.otherTeams}
                  </h2>
                  <TeamsGrid teams={group.items} marker={false} />
                </section>
              ))}
            </div>
          ) : (
            <TeamsGrid teams={filteredTeams} />
          ))}
        </div>
      </section>

      <LeagueFooter />
    </div>
  )
}

// The team cards grid (unchanged markup), for one list or one division group.
function TeamsGrid({ teams, marker = true }: { teams: TeamCard[]; marker?: boolean }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" {...(marker ? sf('teams.list') : {})}>
      {teams.map((team, index) => (
        <Link
          key={team.id}
          to={team.href}
          className="group relative"
          style={{ animationDelay: `${index * 30}ms` }}
        >
          {/* Card */}
          <div className="relative overflow-hidden bg-bg-secondary/60 border border-secondary/30 hover:border-primary/50 transition-all duration-300 group-hover:transform group-hover:scale-[1.02]">
            {/* Logo Container */}
            <div 
              className="relative aspect-[4/3] overflow-hidden flex items-center justify-center p-8"
              style={{
                backgroundColor: team.color ? `${team.color}15` : 'var(--color-secondary-dark-hex)',
              }}
            >
              {team.logoUrl ? (
                <img
                  src={team.logoUrl || ''}
                  alt={team.raw.name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-5xl font-['Montserrat'] font-black text-white/10">
                    {team.code}
                  </span>
                </div>
              )}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/30 to-transparent opacity-60"></div>

              {/* Team Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="font-['Montserrat'] font-black text-white text-base leading-tight mb-1 group-hover:text-primary transition-colors">
                  {team.name}
                </h3>
                {team.city && (
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/60">
                    {team.city}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
